import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Nfc, Users, ScanLine, ShieldAlert, CheckCircle2, MapPin, Loader2, Eraser, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageLoading } from "@/components/shared";
import api from "@/lib/api";

export default function NfcDashboard() {
  const { t } = useTranslation();
  const [acc, setAcc] = useState(undefined);
  const [data, setData] = useState(null);
  const [cleaning, setCleaning] = useState(false);

  const load = () => api.get("/time/nfc-dashboard").then((r) => setData(r.data)).catch(() => setData({ overview: {}, tags: [] }));
  useEffect(() => { api.get("/employer/module/TIME_TRACKING").then((r) => setAcc(r.data?.access || "none")).catch(() => setAcc("none")); }, []);
  useEffect(() => { if (acc === "full") load(); }, [acc]);

  const cleanup = async () => {
    if (!window.confirm(t("nfcd.cleanupConfirm"))) return;
    setCleaning(true);
    try { const r = await api.post("/time/nfc-cleanup"); toast.success(t("nfcd.cleanupDone", { n: r.data.revoked })); await load(); }
    catch { toast.error("Error"); } finally { setCleaning(false); }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/time/suspicious-clockins.csv?days=30", { responseType: "blob" });
      const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = "verdaechtige_erfassungen.csv"; a.click(); URL.revokeObjectURL(u);
    } catch { toast.error("Error"); }
  };

  if (acc === undefined) return <PageLoading />;
  if (acc !== "full") return <Navigate to="/app/employer/time/upgrade" replace />;

  const o = data?.overview || {};
  const stats = [
    { icon: Nfc, tone: "bg-cyan/15 text-cyan-dark", value: o.active_tags, label: t("nfcd.activeTags"), tid: "nfcd-active-tags" },
    { icon: MapPin, tone: "bg-navy/10 text-navy", value: o.sites_with_tags, label: t("nfcd.sitesWithTags"), tid: "nfcd-sites" },
    { icon: ScanLine, tone: "bg-sky-100 text-sky-700", value: o.scans_today, label: t("nfcd.scansToday"), tid: "nfcd-scans-today" },
    { icon: CheckCircle2, tone: "bg-emerald-100 text-emerald-700", value: o.clock_ins_today, label: t("nfcd.clockInsToday"), tid: "nfcd-clockins-today" },
    { icon: Users, tone: "bg-indigo-100 text-indigo-700", value: o.unique_workers_today, label: t("nfcd.uniqueWorkersToday"), tid: "nfcd-workers-today" },
    { icon: ShieldAlert, tone: "bg-red-100 text-red-700", value: o.denied_today, label: t("nfcd.deniedToday"), tid: "nfcd-denied-today" },
    { icon: ShieldAlert, tone: "bg-orange-100 text-orange-700", value: o.blocked_7d, label: t("nfcd.blocked7d"), tid: "nfcd-blocked-7d" },
  ];

  return (
    <div data-testid="nfc-dashboard">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <h1 className="flex items-center gap-2 font-heading text-2xl font-extrabold text-navy sm:text-3xl"><Nfc className="h-6 w-6 text-cyan-dark" /> {t("nfcd.title")}</h1>
        <div className="ml-auto flex items-center gap-2">
          {(data?.tags?.length || 0) > (data?.overview?.sites_with_tags || 0) && (
            <Button onClick={cleanup} disabled={cleaning} variant="outline" className="rounded-full" data-testid="nfcd-cleanup">{cleaning ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Eraser className="mr-1 h-4 w-4" />} {t("nfcd.cleanup")}</Button>
          )}
          <Link to="/app/employer/baustellen" className="flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-sm font-medium text-navy hover:bg-slate-50" data-testid="nfcd-baustellen-link"><MapPin className="h-4 w-4 text-cyan-dark" /> {t("bau.nav")}</Link>
        </div>
      </div>
      <p className="mb-6 text-sm text-slate-500">{t("nfcd.subtitle")}</p>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {stats.map((s) => (
          <div key={s.tid} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={s.tid}>
            <div className={`mb-2 inline-flex h-9 w-9 items-center justify-center rounded-xl ${s.tone}`}><s.icon className="h-5 w-5" /></div>
            <div className="font-heading text-2xl font-extrabold text-navy">{s.value ?? 0}</div>
            <div className="text-xs text-slate-500">{s.label}</div>
          </div>
        ))}
      </div>

      <h2 className="mb-3 mt-8 flex items-center gap-2 font-heading text-lg font-bold text-navy"><Nfc className="h-5 w-5 text-cyan-dark" /> {t("nfcd.perTag")}</h2>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="nfcd-table">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>{["colSite", "colTag", "colScansToday", "colScansTotal", "colWorkers", "colDenied", "colLast"].map((c) => <th key={c} className="px-4 py-3">{t(`nfcd.${c}`)}</th>)}</tr>
          </thead>
          <tbody>
            {!data ? <tr><td colSpan={7} className="p-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></td></tr> :
              data.tags.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-slate-400">{t("nfcd.empty")}</td></tr> :
                data.tags.map((tg) => (
                  <tr key={tg.token_id} className="border-b border-slate-50" data-testid={`nfcd-row-${tg.site_id}`}>
                    <td className="px-4 py-3 font-semibold text-navy"><Link to={`/app/employer/baustellen/${tg.site_id}`} className="text-cyan-dark hover:underline">{tg.site_name}</Link></td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">{tg.tag_id}<span className="ml-1 text-slate-400">#{String(tg.token_id).slice(-4)}</span>{tg.label && <div className="font-sans text-[11px] normal-case text-slate-400">{tg.label}</div>}</td>
                    <td className="px-4 py-3">{tg.scans_today > 0 ? <span className="font-semibold text-sky-700">{tg.scans_today}</span> : <span className="text-slate-400">0</span>}</td>
                    <td className="px-4 py-3 text-slate-600">{tg.scans_total}</td>
                    <td className="px-4 py-3 text-slate-600">{tg.unique_workers}</td>
                    <td className="px-4 py-3">{tg.denied_total > 0 ? <span className="font-semibold text-red-600">{tg.denied_total}</span> : <span className="text-slate-400">0</span>}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{tg.last_used_at ? new Date(tg.last_used_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : t("nfcd.never")}</td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {data?.flagged?.length > 0 && (
        <>
          <h2 className="mb-3 mt-8 flex items-center gap-2 font-heading text-lg font-bold text-red-600" data-testid="nfcd-flagged-title"><ShieldAlert className="h-5 w-5" /> {t("nfcd.flaggedTitle")}</h2>
          <div className="overflow-x-auto rounded-2xl border border-red-200 bg-white">
            <table className="w-full text-sm" data-testid="nfcd-flagged-table">
              <thead className="border-b border-red-100 bg-red-50 text-left text-xs uppercase tracking-wider text-red-400">
                <tr>{["flWorker", "flSite", "flDistance", "flWhen", "flMap"].map((c) => <th key={c} className="px-4 py-3">{t(`nfcd.${c}`)}</th>)}</tr>
              </thead>
              <tbody>
                {data.flagged.map((e, i) => (
                  <tr key={i} className="border-b border-slate-50" data-testid={`nfcd-flagged-${i}`}>
                    <td className="px-4 py-3 font-semibold text-navy">{e.worker_name}</td>
                    <td className="px-4 py-3"><Link to={`/app/employer/baustellen/${e.site_id}`} className="text-cyan-dark hover:underline">{e.site_name}</Link></td>
                    <td className="px-4 py-3"><span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-bold text-red-600">{e.distance_m} m</span></td>
                    <td className="px-4 py-3 text-xs text-slate-500">{e.timestamp ? new Date(e.timestamp).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                    <td className="px-4 py-3">{e.maps_url && <a href={e.maps_url} target="_blank" rel="noreferrer" className="inline-flex items-center text-red-600 hover:underline"><MapPin className="h-4 w-4" /></a>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {data?.blocked?.length > 0 && (
        <>
          <h2 className="mb-3 mt-8 flex items-center gap-2 font-heading text-lg font-bold text-orange-600" data-testid="nfcd-blocked-title"><ShieldAlert className="h-5 w-5" /> {t("nfcd.blockedTitle")}<Button onClick={exportCsv} variant="outline" size="sm" className="ml-auto rounded-full" data-testid="nfcd-export-csv"><Download className="mr-1 h-4 w-4" /> {t("nfcd.exportCsv")}</Button></h2>
          <div className="overflow-x-auto rounded-2xl border border-orange-200 bg-white">
            <table className="w-full text-sm" data-testid="nfcd-blocked-table">
              <thead className="border-b border-orange-100 bg-orange-50 text-left text-xs uppercase tracking-wider text-orange-400">
                <tr>{["blkWorker", "blkSite", "blkReason", "blkDetail", "blkWhen"].map((c) => <th key={c} className="px-4 py-3">{t(`nfcd.${c}`)}</th>)}</tr>
              </thead>
              <tbody>
                {data.blocked.map((e, i) => (
                  <tr key={i} className="border-b border-slate-50" data-testid={`nfcd-blocked-${i}`}>
                    <td className="px-4 py-3 font-semibold text-navy">{e.worker_name}</td>
                    <td className="px-4 py-3">{e.site_id ? <Link to={`/app/employer/baustellen/${e.site_id}`} className="text-cyan-dark hover:underline">{e.site_name}</Link> : e.site_name}</td>
                    <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${e.action === "CLONE_SUSPECTED" ? "bg-orange-100 text-orange-700" : "bg-amber-100 text-amber-700"}`}>{e.action === "CLONE_SUSPECTED" ? t("nfcd.reasonClone") : t("nfcd.reasonGeofence")}</span></td>
                    <td className="px-4 py-3 text-xs text-slate-600">{e.action === "CLONE_SUSPECTED" ? (e.speed_kmh != null ? `${e.speed_kmh} km/h · ${e.distance_m} m` : "—") : (e.distance_m != null ? `${e.distance_m} m` : "—")}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{e.when ? new Date(e.when).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
